// Build forward and inverse relation fields from Drizzle declarations
// FEATURE: zen relation graph builder for code generation
import type {
  FilteredRelationsConfig,
  ThroughRelationPath,
  ThroughRelationsConfig,
} from "./codegen-types";
import type { FKRelationInfo } from "./dsl/adapter";
import { extractFKs } from "./discover";
import type { FKInfo, RelationField, TableInfo } from "./types";
import { APP_SLUG_JS, capitalize } from "./utils";

type RelationTargets = Record<string, Record<string, string>>;

function makePhysicalRelationSignature(
  fields: string[] | null,
  references: string[] | null,
  array: boolean,
) {
  return [array ? "many" : "one", fields?.join(",") || "", references?.join(",") || ""].join("|");
}

function makePhysicalFKSignature(sourceTable: TableInfo, targetTable: TableInfo, fk: FKInfo) {
  return makePhysicalRelationSignature(
    fk.localColumns.map((column) => sourceTable.sqlToJsKey.get(column) || column),
    fk.foreignColumns.map((column) => targetTable.sqlToJsKey.get(column) || column),
    false,
  );
}

function haveReversedPhysicalSignatures(left: RelationField, right: RelationField) {
  if (!left.fields || !left.references || !right.fields || !right.references) {
    return false;
  }

  return (
    left.fields.join(",") === right.references.join(",") &&
    left.references.join(",") === right.fields.join(",")
  );
}

function assertNoRelationFieldCollision(
  table: TableInfo,
  sourceRelations: RelationField[],
  fieldName: string,
) {
  if (table.jsKeySet.has(fieldName)) {
    throw new Error(
      `Relation "${table.exportName}.${fieldName}" collides with an existing field. Rename the FK key in tables.ts or choose a different relation key in relations.ts.`,
    );
  }
  if (sourceRelations.some((relation) => relation.fieldName === fieldName)) {
    throw new Error(
      `Relation "${table.exportName}.${fieldName}" collides with another relation field. Use a unique key in relations.ts.`,
    );
  }
}

function unfoldThroughPath(path: ThroughRelationPath, relationNames: string[] = []): string[] {
  const entries = Object.entries(path);
  if (entries.length !== 1) {
    throw new Error("Through relation path must contain exactly one relation per level.");
  }

  const entry = entries[0]!;
  const [relationName, next] = entry;
  relationNames.push(relationName);

  if (next === true) {
    return relationNames;
  }

  return unfoldThroughPath(next, relationNames);
}

function unfoldRelationSelector(relation: Record<string, true>): string {
  const entries = Object.entries(relation);
  if (entries.length !== 1) {
    throw new Error("Filtered relation selector must contain exactly one relation.");
  }

  const entry = entries[0]!;
  const [relationName, enabled] = entry;
  if (enabled !== true) {
    throw new Error("Filtered relation selector values must be true.");
  }

  return relationName;
}

export function buildRelations(
  tables: TableInfo[],
  relationTargets: RelationTargets = {},
  filteredRelations: FilteredRelationsConfig = {},
  throughRelations: ThroughRelationsConfig = {},
  fkRelations: Record<string, Record<string, FKRelationInfo>> = {},
  options: { ignoreCompositeFkColumns?: string[] } = {},
) {
  const baseTables = tables.filter((table) => !table.derivedFrom);
  const sqlToBaseTable = new Map<string, TableInfo>();
  const modelToTable = new Map<string, TableInfo>();

  for (const table of tables) {
    modelToTable.set(table.modelName, table);
    if (!table.derivedFrom) {
      sqlToBaseTable.set(table.sqlName, table);
    }
  }

  const fksByTable = new Map<string, FKInfo[]>();
  for (const table of baseTables) {
    fksByTable.set(table.modelName, extractFKs(table.table));
  }

  const modelRelations = new Map<string, RelationField[]>();
  for (const table of tables) {
    modelRelations.set(table.modelName, []);
  }

  // Build forward relations from orm.ts FK declarations
  for (const [modelName, relations] of Object.entries(fkRelations)) {
    const sourceTable = modelToTable.get(modelName);
    if (!sourceTable) continue;

    const sourceRelations = modelRelations.get(modelName) || [];
    const colByName = new Map(sourceTable.cfg.columns.map((column) => [column.name, column]));

    for (const [relName, fkInfo] of Object.entries(relations)) {
      const targetTable = modelToTable.get(fkInfo.targetExportName) ?? sqlToBaseTable.get(fkInfo.targetExportName);
      if (!targetTable) continue;

      const targetModelOverride = relationTargets[modelName]?.[relName];
      const fieldName = relName;
      assertNoRelationFieldCollision(sourceTable, sourceRelations, fieldName);

      let hasDefaultFK = false;
      let isOptional = false;
      // fkInfo.fields are JS keys; colByName uses SQL column names
      const jsToSql = new Map(
        [...sourceTable.sqlToJsKey.entries()].map(([sql, js]) => [js, sql]),
      );
      for (const jsKey of fkInfo.fields) {
        const sqlName = jsToSql.get(jsKey) ?? jsKey;
        const localColumn = colByName.get(sqlName);
        if (localColumn?.hasDefault) hasDefaultFK = true;
        if (!localColumn?.notNull) isOptional = true;
      }

      sourceRelations.push({
        fieldName,
        targetModel: targetModelOverride ?? fkInfo.targetExportName,
        optional: isOptional,
        array: false,
        relationName: fkInfo.relationName,
        fields: fkInfo.fields,
        references: fkInfo.references,
        hasDefault: hasDefaultFK,
      });
    }

    modelRelations.set(modelName, sourceRelations);
  }

  for (const table of tables) {
    if (!table.derivedFrom) continue;
    const baseRelations = modelRelations.get(table.derivedFrom) || [];
    modelRelations.set(
      table.modelName,
      baseRelations.map((relation) => ({
        ...relation,
        _opposite: undefined,
      })),
    );
  }

  for (const table of baseTables) {
    const sourceRelations = modelRelations.get(table.modelName) || [];
    const relationsByTarget = new Map<string, RelationField[]>();

    for (const relation of sourceRelations) {
      if (relation.kind && relation.kind !== "normal") continue;
      const group = relationsByTarget.get(relation.targetModel) || [];
      group.push(relation);
      relationsByTarget.set(relation.targetModel, group);
    }

    for (const [targetModel, relations] of relationsByTarget) {
      if (relations.length <= 1) continue;
      for (const relation of relations) {
        if (relation.relationName) continue;
        throw new Error(
          `Ambiguous relations for "${table.exportName}" -> "${targetModel}". Add explicit relationName values in supabase/schema/relations.ts.`,
        );
      }
    }

    const declaredFKRelations = new Set(
      sourceRelations
        .filter((relation) => !relation.array && relation.fields && relation.references)
        .map((relation) =>
          makePhysicalRelationSignature(relation.fields, relation.references, relation.array),
        ),
    );

    for (const fk of fksByTable.get(table.modelName) || []) {
      const targetTable = sqlToBaseTable.get(fk.foreignTable);
      if (!targetTable) continue;

      const signature = makePhysicalFKSignature(table, targetTable, fk);
      if (declaredFKRelations.has(signature)) continue;

      // Try matching after stripping ignored composite FK columns (e.g. app_slug for RLS)
      const ignoreCols = options.ignoreCompositeFkColumns ?? [];
      if (ignoreCols.length > 0 && fk.localColumns.length > 1) {
        const strippedFk: FKInfo = {
          ...fk,
          localColumns: fk.localColumns.filter((c) => !ignoreCols.includes(c)),
          foreignColumns: fk.foreignColumns.filter((_, i) => !ignoreCols.includes(fk.localColumns[i]!)),
        };
        if (strippedFk.localColumns.length > 0) {
          const strippedSig = makePhysicalFKSignature(table, targetTable, strippedFk);
          if (declaredFKRelations.has(strippedSig)) continue;
        }
      }

      console.warn(
        `Warning: undeclared FK "${table.exportName}": [${fk.localColumns.join(", ")}] -> ${targetTable.exportName}([${fk.foreignColumns.join(", ")}]). Add a relation in orm.config.ts or it will be auto-generated.`,
      );
    }
  }

  const forwardByTarget = new Map<string, { source: TableInfo; relation: RelationField }[]>();
  for (const sourceTable of tables) {
    for (const relation of modelRelations.get(sourceTable.modelName) || []) {
      if (relation.kind && relation.kind !== "normal") continue;
      const group = forwardByTarget.get(relation.targetModel) || [];
      group.push({ source: sourceTable, relation });
      forwardByTarget.set(relation.targetModel, group);
    }
  }

  const inverseRelations = new Map<string, RelationField[]>();
  for (const table of tables) {
    const inverses: RelationField[] = [];
    const existingNames = new Set<string>(
      (modelRelations.get(table.modelName) || []).map((relation) => relation.fieldName),
    );

    for (const { source: sourceTable, relation } of forwardByTarget.get(table.modelName) || []) {
      const explicitOpposites = (modelRelations.get(table.modelName) || []).filter(
        (candidate) =>
          (!candidate.kind || candidate.kind === "normal") &&
          candidate.targetModel === sourceTable.modelName &&
          haveReversedPhysicalSignatures(candidate, relation) &&
          (
            !candidate.relationName ||
            !relation.relationName ||
            candidate.relationName === relation.relationName
          ),
      );

      if (explicitOpposites.length > 1) {
        throw new Error(
          `Ambiguous opposite relations for "${sourceTable.exportName}.${relation.fieldName}" on "${table.exportName}". Add explicit relationName values in orm.config.ts.`,
        );
      }

      const explicitOpposite = explicitOpposites[0];
      if (explicitOpposite) {
        relation._opposite = explicitOpposite.fieldName;
        explicitOpposite._opposite = relation.fieldName;
        continue;
      }

      const sourceExport = sourceTable.exportName || sourceTable.sqlName;
      let fieldName = sourceExport.endsWith("s") ? sourceExport : sourceExport + "s";

      if (existingNames.has(fieldName)) {
        const explicitRelation = (modelRelations.get(table.modelName) || []).find(
          (item) => item.fieldName === fieldName,
        );
        if (explicitRelation) {
          relation._opposite = fieldName;
          continue;
        }

        const mainField =
          relation.fields?.find((field) => field !== APP_SLUG_JS) ||
          relation.fields?.[0] ||
          relation.fieldName;
        fieldName = sourceExport + "By" + capitalize(mainField);
      }

      inverses.push({
        fieldName,
        targetModel: sourceTable.modelName,
        optional: false,
        array: true,
        relationName: relation.relationName,
        fields: null,
        references: null,
        hasDefault: false,
        _opposite: relation.fieldName,
      });
      relation._opposite = fieldName;
      existingNames.add(fieldName);
    }

    inverseRelations.set(table.modelName, inverses);
  }

  const getAvailableRelations = (modelName: string) => [
    ...(modelRelations.get(modelName) || []),
    ...(inverseRelations.get(modelName) || []),
  ];

  for (const [modelName, declarations] of Object.entries(filteredRelations)) {
    const sourceTable = modelToTable.get(modelName);
    if (!sourceTable) {
      throw new Error(`Filtered relations declared for unknown model "${modelName}".`);
    }

    const sourceRelations = modelRelations.get(modelName) || [];
    const availableRelations = getAvailableRelations(modelName);

    for (const [fieldName, declaration] of Object.entries(declarations)) {
      const sourceRelation = unfoldRelationSelector(declaration.relation);

      // When filtered relation references itself (polymorphic one() with where),
      // enhance the existing FK relation instead of creating a new one
      const existingFk = sourceRelations.find((r) => r.fieldName === fieldName);
      if (existingFk && sourceRelation === fieldName) {
        existingFk.kind = "filtered";
        existingFk.sourceRelation = sourceRelation;
        existingFk.where = declaration.where;
        existingFk.single = declaration.single;
        continue;
      }

      assertNoRelationFieldCollision(
        sourceTable,
        [...sourceRelations, ...(inverseRelations.get(modelName) || [])],
        fieldName,
      );

      const baseRelation = availableRelations.find(
        (relation) => relation.fieldName === sourceRelation,
      );
      if (!baseRelation) {
        throw new Error(
          `Filtered relation "${modelName}.${fieldName}" references unknown relation "${sourceRelation}".`,
        );
      }

      sourceRelations.push({
        fieldName,
        targetModel: baseRelation.targetModel,
        optional: declaration.single ? true : baseRelation.optional,
        array: declaration.single ? false : baseRelation.array,
        relationName: null,
        fields: baseRelation.fields,
        references: baseRelation.references,
        hasDefault: false,
        kind: "filtered",
        sourceRelation,
        where: declaration.where,
        single: declaration.single,
      });
    }

    modelRelations.set(modelName, sourceRelations);
  }

  for (const [modelName, declarations] of Object.entries(throughRelations)) {
    const sourceTable = modelToTable.get(modelName);
    if (!sourceTable) {
      throw new Error(`Through relations declared for unknown model "${modelName}".`);
    }

    const sourceRelations = modelRelations.get(modelName) || [];

    for (const [fieldName, declaration] of Object.entries(declarations)) {
      assertNoRelationFieldCollision(
        sourceTable,
        [...sourceRelations, ...(inverseRelations.get(modelName) || [])],
        fieldName,
      );

      const throughPath = unfoldThroughPath(declaration.path);
      let currentModel = modelName;
      let targetRelation: RelationField | undefined;
      let isMany = false;

      for (const relationName of throughPath) {
        const relation = getAvailableRelations(currentModel).find(
          (item) => item.fieldName === relationName,
        );
        if (!relation) {
          throw new Error(
            `Through relation "${modelName}.${fieldName}" references unknown relation "${currentModel}.${relationName}".`,
          );
        }

        targetRelation = relation;
        currentModel = relation.targetModel;
        if (relation.array) {
          isMany = true;
        }
      }

      if (!targetRelation) {
        throw new Error(`Through relation "${modelName}.${fieldName}" is missing a target path.`);
      }

      sourceRelations.push({
        fieldName,
        targetModel: targetRelation.targetModel,
        optional: !isMany,
        array: isMany,
        relationName: null,
        fields: null,
        references: null,
        hasDefault: false,
        kind: "through",
        throughPath,
      });
    }

    modelRelations.set(modelName, sourceRelations);
  }

  return { modelRelations, inverseRelations };
}
